import { useRef, useState } from 'react';
import { schedulePreviews, FIRST_PACK_PAID_DAYS_AGO } from '../data/mockSubscriptionSchedules';
import type { SchedulePreviewId } from '../data/mockSubscriptionSchedules';
import type { Order } from '../components/OrdersTable/OrdersTable';
import { addDays, formatMediumDate, formatShortDate, getScheduleLabel, getScheduledDatesAfter, getTimePart, isOnSchedule, parseScheduleDate, startOfDay, toDateKey, toDateTimeValue } from '../utils/subscriptionSchedule';

export const OFF_SCHEDULE_WARNING = "This moves the member off the pack's release schedule. Future charges will follow the new date until you put them back on schedule.";

interface UseBillingSchedulePreviewOptions {
  nextPayment?: string;
  applyNextPayment: (value: string) => void;
}

const getSchedule = (id: SchedulePreviewId) => (schedulePreviews.find(preview => preview.id === id) ?? schedulePreviews[0]).schedule;

export function useBillingSchedulePreview({
  nextPayment,
  applyNextPayment
}: UseBillingSchedulePreviewOptions) {
  const [previewId, setPreviewId] = useState<SchedulePreviewId>('rolling');
  const [firstPackHeld, setFirstPackHeldState] = useState(false);
  // The subscription's own date, restored when switching back to Rolling
  const rollingNextPayment = useRef<string | null>(null);

  const schedule = getSchedule(previewId);
  const isSynced = schedule.mode !== 'rolling';
  const today = startOfDay(new Date());
  const timePart = getTimePart(nextPayment);

  // A held first pack is already paid for, so the first scheduled charge is the release after
  const getExpectedNext = (id: SchedulePreviewId, held: boolean) => {
    const target = getSchedule(id);
    const [first, second] = getScheduledDatesAfter(target, today, 2);
    return target.mode === 'releaseDates' && held ? second : first;
  };

  const selectPreview = (id: SchedulePreviewId) => {
    if (id === previewId) return;
    if (previewId === 'rolling') rollingNextPayment.current = nextPayment ?? '';
    if (id === 'rolling') {
      applyNextPayment(rollingNextPayment.current ?? nextPayment ?? '');
      setFirstPackHeldState(false);
    } else {
      const held = id === 'releaseDates' && firstPackHeld;
      const expected = getExpectedNext(id, held);
      if (expected) applyNextPayment(toDateTimeValue(expected, timePart));
      if (id !== 'releaseDates') setFirstPackHeldState(false);
    }
    setPreviewId(id);
  };

  const setFirstPackHeld = (held: boolean) => {
    setFirstPackHeldState(held);
    const expected = getExpectedNext(previewId, held);
    if (expected) applyNextPayment(toDateTimeValue(expected, timePart));
  };

  const holdsFirstPack = schedule.mode === 'releaseDates' && firstPackHeld;
  const expectedNext = isSynced ? getExpectedNext(previewId, firstPackHeld) : undefined;
  const nextDate = parseScheduleDate(nextPayment);
  const isOffSchedule = Boolean(isSynced && nextDate && !isOnSchedule(schedule, nextDate));

  const putBackOnSchedule = () => {
    if (expectedNext) applyNextPayment(toDateTimeValue(expectedNext, timePart));
  };

  const isDateOnSchedule = (date: Date) => !isSynced || isOnSchedule(schedule, date);
  const highlightedDates = isSynced ? getScheduledDatesAfter(schedule, addDays(today, -1), schedule.mode === 'fixedDay' ? 24 : 8) : [];

  // Upcoming releases from the next charge; off-schedule members follow their own date instead
  const releasesFromNext = schedule.mode === 'releaseDates' && nextDate && !isOffSchedule ? getScheduledDatesAfter(schedule, addDays(nextDate, -1), 6) : [];

  const decorateUpcomingOrders = (orders: Order[]): Order[] => {
    if (releasesFromNext.length < 2) return orders;
    return orders.slice(0, releasesFromNext.length - 1).map((order, index) => {
      const release = releasesFromNext[index];
      return {
        ...order,
        date: formatMediumDate(release),
        releaseLabel: `${formatMediumDate(release)} release`,
        skip: index === 0 ? {
          label: `Skip the ${formatShortDate(release)} release`,
          confirmMessage: `Their next charge will be the ${formatMediumDate(releasesFromNext[1])} release.`
        } : undefined
      };
    });
  };

  const skipNextRelease = () => {
    const following = releasesFromNext[1];
    if (following) applyNextPayment(toDateTimeValue(following, timePart));
  };

  const paidDate = addDays(today, -FIRST_PACK_PAID_DAYS_AGO);
  const heldRelease = holdsFirstPack ? getScheduledDatesAfter(schedule, today, 1)[0] : undefined;

  const decorateRecentOrders = (orders: Order[]): Order[] => holdsFirstPack ? orders.map(order => order.isCheckout ? {
    ...order,
    date: toDateKey(paidDate),
    heldForRelease: true
  } : order) : orders;

  // Order Now on a synced pack takes the place of the next scheduled charge
  const orderNowBase = !isOffSchedule && nextDate ? nextDate : expectedNext;
  const orderNowFollowing = isSynced && orderNowBase ? getScheduledDatesAfter(schedule, orderNowBase, 1)[0] : undefined;
  const orderNowSummary = orderNowBase && orderNowFollowing ? schedule.mode === 'releaseDates' ? `This order uses the ${formatShortDate(orderNowBase)} release. Next charge ${formatMediumDate(orderNowFollowing)}.` : `This order replaces the ${formatShortDate(orderNowBase)} charge. Next charge ${formatMediumDate(orderNowFollowing)}.` : null;

  const heldBannerMessage = heldRelease ? `First pack ships with the ${formatShortDate(heldRelease)} release. Paid on ${formatShortDate(paidDate)}.` : null;

  return {
    previewId,
    selectPreview,
    firstPackHeld,
    setFirstPackHeld,
    isSynced,
    isReleaseMode: schedule.mode === 'releaseDates',
    scheduleLabel: getScheduleLabel(schedule),
    isOffSchedule,
    expectedNextLabel: expectedNext ? formatShortDate(expectedNext) : null,
    putBackOnSchedule,
    isDateOnSchedule,
    highlightedDates,
    decorateUpcomingOrders,
    decorateRecentOrders,
    skipNextRelease,
    orderNowSummary,
    heldBannerMessage
  };
}
